import { translateCognitoError } from '../../utils/auth-utils';
import { Component, OnInit, AfterViewInit } from '@angular/core';
import { initFlowbite } from 'flowbite';
import { FavoritesService } from '../../services/favorites/favorites.service';
import { RealEstateService } from '../../services/real-estate/real-estate.service';
import { RealEstate } from '../../interfaces/real-estate';
import { RealEstateCardComponent } from '../real-estate-card/real-estate-card.component';
import { BehaviorSubject, Observable, forkJoin, of } from 'rxjs';
import {map, catchError} from 'rxjs/operators';
import {AsyncPipe, CurrencyPipe, DatePipe, CommonModule} from '@angular/common';
import {Base64Helper} from '../../utils/base64-helper';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { fetchUserAttributes, updateUserAttributes, updatePassword } from '@aws-amplify/auth';
import { StripeService, StripeSubscription, StripePaymentMethod } from '../../services/stripe/stripe.service';
import { NotificationService } from '../../services/notification/notification.service';
import { Notification } from '../../interfaces/notification';
import { NotificationModalComponent } from '../notification-modal/notification-modal.component';
import { AccountCancelModalComponent } from '../account-cancel-modal/account-cancel-modal.component';
import { AuthenticationService } from '../../services/authentication/authentication.service';
import { MarketingConsentService } from '../../services/marketing-consent/marketing-consent.service';
import {ActivatedRoute, Router} from '@angular/router';

@Component({
  selector: 'app-user',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, AsyncPipe, DatePipe, CurrencyPipe, RealEstateCardComponent, NotificationModalComponent, AccountCancelModalComponent],
  templateUrl: './user.component.html',
  styleUrl: './user.component.css'
})
export class UserComponent implements OnInit, AfterViewInit {
  activeTab: 'perfil' | 'notificacoes' = 'perfil';
  readonly accountTabs: { id: 'perfil' | 'notificacoes'; label: string }[] = [
    { id: 'perfil', label: 'Dados pessoais & plano' },
    { id: 'notificacoes', label: 'Notificações' }
  ];
  showEditPopup = false;
  favoriteRealEstates = new BehaviorSubject<RealEstate[]>([]);
  favoriteRealEstates$: Observable<RealEstate[]> = this.favoriteRealEstates.asObservable();
  loading = false;

  // Profile form
  profileForm: FormGroup;
  saving = false;
  saveSuccess = '';
  saveError = '';
  verificationMessage = '';

  // Change password form
  passwordForm: FormGroup;
  pwdSaving = false;
  pwdSuccess = '';
  pwdError = '';

  // Credit card form
  cardForm: FormGroup;
  cardSaving = false;
  cardSuccess = '';
  cardError = '';

  // Stripe data
  stripeLoading = false;
  stripeError = '';
  stripeCustomer: any = null;
  stripeSubscriptions: StripeSubscription[] = [];
  stripePaymentMethods: StripePaymentMethod[] = [];

  userAttributes: any = {
    email: '',
    name: '',
    family_name: '',
    city: '',
    state: ''
  };

  // Notifications data
  notifications: Notification[] = [];
  showNotificationModal = false;
  selectedNotification: Notification | null = null;
  showDeleteNotificationModal = false;
  notificationToDeleteId: string | null = null;

  // Account cancellation
  showCancelModal = false;
  cancellingAccount = false;
  cancelError = '';

  constructor(
    private favoritesService: FavoritesService,
    private realEstateService: RealEstateService,
    private fb: FormBuilder,
    private stripeService: StripeService,
    private notificationService: NotificationService,
    private marketingConsentService: MarketingConsentService,
    private route: ActivatedRoute,
    private router: Router
  ) {
    this.profileForm = this.fb.group({
      email: ['', [Validators.email]],
      name: [''],
      family_name: [''],
      city: [''],
      state: [''],
      newsletter: [false],
      real_estate_alerts: [false]
    });

    this.passwordForm = this.fb.group({
      currentPassword: ['', [Validators.required]],
      newPassword: ['', [Validators.required, Validators.minLength(7)]],
      confirmNewPassword: ['', [Validators.required]]
    }, { validators: this.passwordsMatchValidator });

    // Initialize credit card form
    this.cardForm = this.fb.group({
      cardholder: ['', [Validators.required]],
      number: ['', [Validators.required, Validators.pattern(/^\d{13,19}$/)]],
      expDate: ['', [Validators.required, Validators.pattern(/^(0?[1-9]|1[0-2])\/(\d{2})$/)]],
      cvc: ['', [Validators.required, Validators.pattern(/^\d{3,4}$/)]]
    });
  }

  ngOnInit(): void {
    // Initialize Flowbite after component is loaded
    setTimeout(() => {
      initFlowbite();
    }, 0);

    this.route.queryParamMap.subscribe(params => {
      const tab = params.get('tab');
      const routeTab = this.route.snapshot.data['tab'];
      this.switchTab(tab === 'notificacoes' || routeTab === 'notificacoes' ? 'notificacoes' : 'perfil');
    });

    // Load current Cognito user attributes into the form
    this.loadUserAttributes();

    // Load Stripe information for the user
    this.loadStripeInfo();

    // Load notifications
    this.loadNotifications();
    this.notificationService.notificationsObservable.subscribe(notifications => {
      this.notifications = notifications;
    });

    // Subscribe to favorites and load real estates when favorites change
    this.favoritesService.favoritesObservable.subscribe(favoriteIds => {
      if (favoriteIds && favoriteIds.length > 0) {
        this.loadFavoriteRealEstates(favoriteIds);
      } else {
        this.favoriteRealEstates.next([]);
      }
    });

    // Initial load of favorites
    this.favoritesService.getFavorites();
  }

  loadStripeInfo(): void {
    this.stripeLoading = true;
    this.stripeError = '';
    this.stripeCustomer = null;
    this.stripeSubscriptions = [];
    this.stripePaymentMethods = [];

    this.stripeService.getUserStripe().subscribe({
      next: (data) => {
        console.log('Stripe info loaded:', data);
        const normalized = this.normalizeStripeResponse(data || {});
        this.stripeCustomer = normalized.customer || null;
        this.stripeSubscriptions = normalized.subscriptions || [];
        this.stripePaymentMethods = normalized.paymentMethods || [];
      },
      error: (err) => {
        console.error('Failed to load Stripe info', err);
        this.stripeError = 'Não foi possível carregar suas assinaturas no momento.';
      },
      complete: () => {
        this.stripeLoading = false;
      }
    });
  }

  private normalizeStripeResponse(data: any): { customer: any; subscriptions: StripeSubscription[]; paymentMethods: StripePaymentMethod[] } {
    const outSubs: StripeSubscription[] = [];
    const rawSubs: any[] = Array.isArray(data?.subscriptions) ? data.subscriptions : [];

    for (const s of rawSubs) {
      // Detect new simplified shape if items is an array with objects containing name/price/frequency
      const isSimplified = Array.isArray(s?.items) && !s?.items?.data;

      if (isSimplified) {
        const normalizedItems = (s.items as any[]).map((it: any, idx: number) => {
          const price = {
            id: it?.priceId ?? undefined,
            nickname: it?.name ?? it?.priceId ?? undefined,
            unit_amount: it?.price?.amount ?? undefined,
            currency: it?.price?.currency ?? undefined,
            recurring: {
              interval: it?.frequency?.interval ?? undefined,
              interval_count: it?.frequency?.interval_count ?? undefined,
            },
          } as any;
          return {
            id: it?.priceId || `item_${idx}`,
            price,
            current_period_end: it?.current_period_end ?? undefined,
            current_period_start: it?.current_period_start ?? undefined,
          } as any;
        });

        // Determine subscription-level period fields: prefer top-level if provided, otherwise first item's
        const firstItem = normalizedItems?.[0];
        outSubs.push({
          id: s?.id,
          status: s?.status,
          current_period_end: s?.current_period_end ?? firstItem?.current_period_end ?? undefined,
          current_period_start: s?.current_period_start ?? firstItem?.current_period_start ?? undefined,
          cancel_at: !!s?.cancel_at,
          trial_days_left: s?.trial_days_left,
          items: { data: normalizedItems },
        } as StripeSubscription);
      } else {
        // Assume old Stripe-like shape; pass through
        outSubs.push({
          ...s,
          trial_days_left: s?.trial_days_left,
        } as StripeSubscription);
      }
    }

    const outPMs: StripePaymentMethod[] = [];
    const rawPMs: any[] = Array.isArray(data?.paymentMethods) ? data.paymentMethods : [];
    for (const pm of rawPMs) {
      // New simplified payment method if it has brand/last4 at top-level and no card object
      if (!pm?.card && (pm?.brand || pm?.last4 || pm?.exp_month || pm?.exp_year)) {
        outPMs.push({
          card: {
            brand: pm?.brand,
            last4: pm?.last4,
            exp_month: pm?.exp_month,
            exp_year: pm?.exp_year,
          },
        } as StripePaymentMethod);
      } else {
        outPMs.push(pm as StripePaymentMethod);
      }
    }

    return {
      customer: data?.customer ?? null,
      subscriptions: outSubs,
      paymentMethods: outPMs,
    };
  }

   ngAfterViewInit(): void {
    // Initialize Flowbite tabs
    setTimeout(() => {
      initFlowbite();
      // Set default active tab
      this.switchTab('perfil');
    }, 0);
  }

  loadFavoriteRealEstates(favoriteIds: string[]): void {
    this.loading = true;
    console.log(`Starting to load ${favoriteIds.length} favorite real estates`);

    // Handle the case when there are no favorite IDs
    if (favoriteIds.length === 0) {
      console.log('No favorite real estates to load');
      this.favoriteRealEstates.next([]);
      this.loading = false;
      return;
    }

    // Create an array of observables for each real estate request
    let realEstateObservable = favoriteIds.map((id, index) => {
      console.log(`[${index + 1}/${favoriteIds.length}] Starting request for real estate ID: ${id}`);
      return this.realEstateService.getRealEstate(Base64Helper.encode(id)).pipe(
        map(result => {
          console.log(`[${index + 1}/${favoriteIds.length}] Successfully fetched real estate ID: ${id}`);
          return result;
        }),
        catchError(error => {
          console.error(`[${index + 1}/${favoriteIds.length}] Error fetching real estate with ID ${id}:`, error);
          return of(null); // Return null for failed requests
        })
      );
    });

    console.log(`Created ${realEstateObservable.length} observables for ${favoriteIds.length} requests`);

    // Use forkJoin to wait for all requests to complete
    forkJoin(realEstateObservable).pipe(
      map(realEstates => {
        console.log(`All ${favoriteIds.length} requests completed. Results:`, realEstates);
        // Filter out any null values and cast to RealEstate array
        const filteredRealEstates = realEstates.filter(estate => estate !== null);
        console.log(`${filteredRealEstates.length} valid real estates found after filtering nulls`);
        return (filteredRealEstates as RealEstate[]);
      })
    ).subscribe({
      next: (realEstates) => {
        console.log(`Successfully loaded ${realEstates.length} favorite real estates`);
        // Update the favorites list with the loaded real estates
        this.favoriteRealEstates.next(realEstates);
        this.loading = false;
      },
      error: (error) => {
        // Handle any errors that occur while loading
        console.error('Error loading favorite real estates:', error);
        this.loading = false;
        // Even on error, update the favorites list with an empty array
        this.favoriteRealEstates.next([]);
      },
      complete: () => {
        console.log('ForkJoin operation completed');
      }
    });
  }

  isArray(val: any): boolean {
    return Array.isArray(val);
  }

  // Method to manually switch tabs
  switchTab(tabId: 'perfil' | 'notificacoes'): void {
    this.activeTab = tabId;
  }

  onMobileTabChange(event: Event): void {
    const value = (event.target as HTMLSelectElement).value as 'perfil' | 'notificacoes';
    this.switchTab(value);
  }

  // Notifications Methods
  loadNotifications(): void {
    this.notificationService.getNotifications();
  }

  openCreateNotificationModal(): void {
    this.selectedNotification = null;
    this.showNotificationModal = true;
  }

  openEditNotificationModal(notification: Notification): void {
    this.selectedNotification = notification;
    this.showNotificationModal = true;
  }

  closeNotificationModal(): void {
    this.showNotificationModal = false;
  }

  onNotificationSaved(notification: Notification): void {
    this.loadNotifications();
  }

  requestDeleteNotification(notificationId: string): void {
    this.notificationToDeleteId = notificationId;
    this.showDeleteNotificationModal = true;
  }

  cancelDeleteNotification(): void {
    this.showDeleteNotificationModal = false;
    this.notificationToDeleteId = null;
  }

  confirmDeleteNotification(): void {
    if (!this.notificationToDeleteId) {
      return;
    }
    const id = this.notificationToDeleteId;
    this.notificationService.deleteNotification(id)
      .then(() => {
        this.loadNotifications();
      })
      .catch(error => {
        console.error('Error deleting notification:', error);
      })
      .finally(() => {
        this.cancelDeleteNotification();
      });
  }

  async loadUserAttributes(): Promise<void> {
    try {
      this.loading = true;
      const attrs = await fetchUserAttributes();
      const formValues = {
        email: attrs?.email ?? '',
        name: attrs?.name ?? '',
        family_name: attrs?.family_name ?? '',
        city: (attrs as any)['custom:city'] ?? '',
        state: (attrs as any)['custom:state'] ?? '',
        newsletter: (attrs as any)['custom:newsletter'] === 'true',
        real_estate_alerts: (attrs as any)['custom:real-estate-alerts'] === 'true'
      };
      this.profileForm.patchValue(formValues);

      this.userAttributes = attrs;

    } catch (err) {
      console.error('Failed to load user attributes', err);
      this.saveError = 'Não foi possível carregar seus dados no momento.';
    } finally {
      this.loading = false;
    }
  }

  async saveProfile(): Promise<void> {
    this.saveSuccess = '';
    this.saveError = '';
    this.verificationMessage = '';

    if (this.profileForm.invalid) {
      this.saveError = 'Por favor, corrija os campos inválidos.';
      return;
    }

    const v = this.profileForm.value;

    // Build attributes object. Only include fields that have values to avoid overwriting with empty strings.
    const attributes: Record<string, string> = {};
    const setIf = (key: string, value?: string) => {
      if (value !== undefined && value !== null && String(value).trim() !== '') {
        attributes[key] = String(value).trim();
      }
    };

    setIf('email', v.email);
    setIf('name', v.name);
    setIf('family_name', v.family_name);
    setIf('custom:city', v.city);
    setIf('custom:state', v.state);

    try {
      this.saving = true;
      const result = await updateUserAttributes({ userAttributes: attributes });

      // Amplify v6 returns an object mapping attribute -> next step
      // We will assemble a message if any attribute requires verification (e.g., email/phone).
      const requiringVerification = Object.entries(result).filter(([, step]: any) => step?.nextStep === 'CONFIRM_ATTRIBUTE_WITH_CODE' || step?.deliveryMedium || step?.codeDeliveryDetails);
      if (requiringVerification.length > 0) {
        const list = requiringVerification.map(([attr]) => attr).join(', ');
        this.verificationMessage = `Verifique seu(s) atributo(s): ${list}. Enviamos um código de confirmação (verifique seu e-mail/telefone).`;
      }

      this.saveSuccess = 'Seus dados foram atualizados com sucesso.';
    } catch (err: any) {
      console.error('Failed to update user attributes', err);
      this.saveError = translateCognitoError(err, err?.message || 'Falha ao atualizar seus dados. Tente novamente mais tarde.');
    } finally {
      this.saving = false;
    }
  }

  // Validator to ensure new password and confirmation match
  private passwordsMatchValidator = (group: FormGroup) => {
    const np = group.get('newPassword')?.value;
    const cp = group.get('confirmNewPassword')?.value;
    return np && cp && np === cp ? null : { passwordMismatch: true };
  };

  // Handler to update the user's password
  async changePassword(): Promise<void> {
    this.pwdSuccess = '';
    this.pwdError = '';

    if (this.passwordForm.invalid) {
      if (this.passwordForm.errors?.['passwordMismatch']) {
        this.pwdError = 'As novas senhas não coincidem.';
      } else {
        this.pwdError = 'Preencha todos os campos corretamente.';
      }
      return;
    }

    const currentPassword = this.passwordForm.get('currentPassword')?.value;
    const newPassword = this.passwordForm.get('newPassword')?.value;

    try {
      this.pwdSaving = true;
      await updatePassword({ oldPassword: currentPassword, newPassword });
      this.pwdSuccess = 'Senha alterada com sucesso.';
      this.passwordForm.reset();
    } catch (err: any) {
      console.error('Failed to change password', err);
      this.pwdError = translateCognitoError(err, err?.message || 'Não foi possível alterar a senha. Tente novamente mais tarde.');
    } finally {
      this.pwdSaving = false;
    }
  }

  async updateNewsletter(event: Event): Promise<void> {
    const checkbox = event.target as HTMLInputElement;
    const enabled = checkbox.checked;
    try {
      await this.marketingConsentService.saveDecision(enabled);
    } catch (error) {
      console.error('Failed to update newsletter preference', error);
      // Revert checkbox state if needed, or show error
      checkbox.checked = !enabled;
    }
  }

  async updateRealEstateAlerts(event: Event): Promise<void> {
    const checkbox = event.target as HTMLInputElement;
    const enabled = checkbox.checked;
    try {
      await this.marketingConsentService.saveRealEstateAlerts(enabled);
    } catch (error) {
      console.error('Failed to update real estate alerts preference', error);
      // Revert checkbox state if needed, or show error
      checkbox.checked = !enabled;
    }
  }

  // Placeholder handler for saving card data.
  async saveCard(): Promise<void> {
    this.cardSuccess = '';
    this.cardError = '';

    if (this.cardForm.invalid) {
      this.cardError = 'Preencha os dados do cartão corretamente.';
      return;
    }

    try {
      this.cardSaving = true;
      // TODO: Integrate with StripeService to create/attach a payment method.
      await new Promise(resolve => setTimeout(resolve, 600));
      this.cardSuccess = 'Cartão atualizado com sucesso.';
      // Optionally reset sensitive fields
      this.cardForm.patchValue({ number: '', expDate: '', cvc: '' });
    } catch (err: any) {
      console.error('Failed to update card', err);
      this.cardError = err?.message || 'Não foi possível atualizar o cartão. Tente novamente mais tarde.';
    } finally {
      this.cardSaving = false;
    }
  }

  openEditPopup(): void {
    this.showEditPopup = true;
  }

  closeEditPopup(): void {
    this.showEditPopup = false;
  }

  // Trigger backend to create a Stripe portal/checkout session and redirect user
  openPaymentPortal(): void {
    // Optionally, we could set a loading state; for now just call and redirect
    this.stripeService.createOrGetStripePortal().subscribe({
      next: (res: any) => {
        const url = res?.url || res?.stripeUrl || res?.sessionUrl;
        if (url && typeof url === 'string') {
          window.location.href = url;
        } else {
          console.error('Stripe portal URL not found in response', res);
          alert('Não foi possível abrir a página de pagamento agora. Tente novamente mais tarde.');
        }
      },
      error: (err) => {
        console.error('Failed to create/get Stripe portal', err);
        alert('Não foi possível abrir a página de pagamento agora. Tente novamente mais tarde.');
      }
    });
  }

  openCancelModal(): void {
    this.showCancelModal = true;
    this.cancelError = '';
  }

  closeCancelModal(): void {
    this.showCancelModal = false;
  }

  confirmCancelSubscription(): void {
    this.cancellingAccount = true;
    this.cancelError = '';
    this.stripeService.createOrGetStripePortal().subscribe({
      next: (res: any) => {
        this.cancellingAccount = false;
        const url = res?.url || res?.stripeUrl || res?.sessionUrl;
        if (url && typeof url === 'string') {
          this.showCancelModal = false;
          window.location.href = url;
        } else {
          console.error('Portal URL not found in response', res);
          this.cancelError = 'Não foi possível carregar o link. Tente novamente mais tarde.';
        }
      },
      error: (err) => {
        console.error('Failed to get portal link', err);
        this.cancelError = 'Ocorreu um erro ao carregar o link. Por favor, tente novamente mais tarde.';
        this.cancellingAccount = false;
      }
    });
  }

  reactivatePlan(): void {
    this.router.navigate(['/planos']);
  }
}
